import { CameraHealthItem } from './camera';
import { LocationProperties } from './impl';
import { LocationBaseResponse } from './location';
import type { Pagination } from './pagination';
import { InvestigationTransactionDetails, ReviewSubjectType, TheftType } from './review';
import { BranchRole, LocationRole } from './user';

/**
 * This is a custom object format we use
 * just for displaying sold items & cancellations in the same view
 */
export type SoldItemsDisplayItem = {
  occurred: string;
  date: string;
  items: string[];
};

export interface MissingFace {
  img_id: string;
  location_name: string;
  user_name: string;
  total?: number;
  cancel_date: string;
  branch: string;
  theft_category: string;
  is_theft?: boolean;
}

export interface SuspectIdentity {
  face_id: string;
}

export interface TransactionVideo {
  cancellation_uuid: string;
  cancellation_id: string;
  cancel_date: string;
  location_properties_id: string;
  location_timezone: string;
  location_bandwidth: string;
  location_camera_id: string;
  camera_uuid: string;
  camera_lag_s: string;
  location_video_id: string;
  latest_request_status: string;
  latest_request_id: string;
  clip_url: string;
}

export interface LocationBasic {
  name: string;
  id: number;
  branch_id: number;
  branch_name: string;
}

export interface LocationWithProps {
  location_name: string;
  branch_name: string;
  branch_id: number;
  active: boolean;
  scrape: boolean;
  location_id: number;
  stripe_price_id: string;
  has_props: boolean;
  num_cameras: number;
  daily_snapshots_enabled: boolean | null;
  location_properties: LocationProperties;
}

export interface TransactionItem {
  item_row_id: string;
  tr_id: number;
  image_id: string;
  location_id: number;
  location_name: string;
  branch_id: number;
  branch_name: string;
  is_cancellation: boolean;
  is_theft: boolean;
  tr_ts: string;
  tr_ts_unix?: number;
  user_name?: string | null;
  action_type?: string;
  description?: string;
  price?: number;
  total?: number;
}

export type TransactionsStreamEvent =
  | { readonly type: 'summary'; readonly totalCount: number }
  | { readonly type: 'transaction'; readonly transaction: TransactionItem };

export interface GetTransactionsFilters {
  from_date: Date;
  to_date: Date;
  location_ids?: number[];
}

export interface TheftDashboardApiRow {
  loc_name: string;
  can_count: number;
  can_total: number;
  can_theft: number;
  trs_count: number;
  trs_sum: number;
  cam_count: number;
}

export interface TheftDashboardFilters {
  branch_ids: number[];
  from_date: Date;
  to_date: Date;
  estimated_savings_percent?: number;
}

export interface RhombusCamera {
  camera_uuid: string;
  rhombus_camera_name: string;
  location_id: number;
  live_stream_link: string;
  password: string;
}

export enum TheftReportAttachmentType {
  /**
   * Direct link to Rhombus shared video clip
   */
  VideoLink = 'VideoLink',
  /**
   * Direct link to Panoptyc cancellation transaction
   */
  PanoptycLink = 'PanoptycLink',
  /**
   * URI for screenshot of the report / cancellation
   */
  ReportImage = 'ReportImage',
  /**
   * URI for cropped screenshot of the suspect
   */
  InstancePhoto = 'InstancePhoto',
  /**
   * Generic file attachment (e.g. .orchid export from Wawa/IPConfigure)
   */
  Attachment = 'Attachment',
}

export interface TheftReportAttachment {
  content: string;
  id: string;
  password: string;
  review_outcome_id: string;
  type: TheftReportAttachmentType;
  download_url?: string;
}

export type TheftReport = {
  attachments?: TheftReportAttachment[];
  case_num: number;
  case_id: string;
  comments: string;
  date_of_instance: string;
  date_shared: string | null;
  id: string;
  review_subject: ReviewSubjectType;
  review_id: string;
  instance_id: string | null;
  instance_num: number;
  /** This can now be null if `branch.hide_currency` is `true` */
  item_cancellation_value: string | null;
  /**
   * Note:
   * it seems that this value can be 0, which doesn't exist,
   * and probably should have been `null`
   */
  location_id: number;
  video_links?: string[];
  is_self_service?: boolean;
  use_kiosk_photo?: boolean;
  location_timezone?: string;
  image_id?: string;
  asset_recovery_id: string | null;
  asset_recovery_created_at: string | null;
};

export type SharedInvestigation = Omit<
  TheftReport,
  'case_num' | 'case_id' | 'instance_id' | 'instance_num' | 'location_id' | 'location_timezone'
> & {
  location: LocationBaseResponse;
  case: TheftReportCase;
  timeline: InvestigationTransactionDetails[];
  actor?: string;
  theft_type: TheftType;
};

// A file attachment rendered on a shared investigation (a Wawa case video or an orchid bundle).
export type FileAttachment = {
  url: string;
  name: string;
  thumbnailUrl: string;
  hasThumbnail: boolean;
  // Presigned S3 link used for downloading; falls back to url when not provided.
  downloadUrl: string;
};

export interface TheftReportCase {
  id: string;
  instance_num: number;
  num: number;
}

export interface GetTheftReportRes {
  branch_name: string;
  branch_id: number;
  date_range: string | null;
  location_name: string;
  location_id: number;
  completed_count: number;
  hide_currency: boolean;
  theft_reports: TheftReport[];
  location_timezone?: string;
  asset_recovery_enabled: boolean;
}

export interface TheftReportSharedLink {
  id: string; // Guid is represented as string in TypeScript
  location_id: number;
  date_from?: Date | null; // DateTime? can be Date or null
  date_to?: Date | null; // DateTime? can be Date or null
  created_at: Date; // DateTime is represented as Date in TypeScript
  share_key: string;
  share_link_relative?: string;
  share_link_absolute?: string;
  date_range_string?: string | null;
}

export interface LocationTheftReport {
  location_name: string;
  location_id: number;
  external_location_id?: string;
  branch_id?: number;
  theft_found?: boolean | null;
  no_theft_found: boolean;
  theft_count: number;
  qa_theft_count: number;
  repeat_offenders_count: number;
  // shared theft report keys & links
  all_theft_share_key?: string | null;
  all_theft_shared_link_absolute?: string | null;
  all_theft_shared_link_relative?: string | null;
  most_recent_share_key?: string | null;
  most_recent_shared_link_absolute?: string | null;
  most_recent_shared_link_relative?: string | null;
  // "most recent theft report"-specific information
  most_recent_date_from?: string | null;
  most_recent_date_to?: string | null;
  // dates (stringified variants are meant for export logic, but including here for reference)
  date_of_last_incident?: string | null;
  date_of_last_incident_string?: string | null;
  most_recent_date_shared?: string | null;
  most_recent_date_shared_string?: string | null;
  most_recent_report_sent_date?: string | null;
  most_recent_report_sent_date_string?: string | null;
}

export interface GetTheftReportsFilters extends Record<string, string | string[]> {
  case_id?: string;
  case_ids?: string[];
  date_shared_from: string;
  date_shared_to: string;
}

export type GetTheftReportsOpts = GetTheftReportsFilters & { api_key?: string };

export interface TransactionByFaceId {
  face_id: string;
  total: number;
  num_items: number;
  image_id: string;
  location_name: string;
  user_name: string;
  cancel_date?: string | null;
  is_theft?: boolean | null;
  branch: string;
  count?: number | null;
  last_seen?: string | null;
  is_shared?: boolean | null;
  theft_category?: string | null;
  num_comments?: number;
  num_videos?: number;
  theftLabel?: string;
  timerSubscription?: ReturnType<typeof setInterval>;
}

export interface TransactionsByFaceIdResult {
  transactions: Pagination<TransactionByFaceId>;
  total: number;
  total_count: number;
  running_total: number;
  latest_cancel_date?: string | null;
  theft_category?: 'High' | 'Medium' | 'Low' | null;
}

export interface ReportPortalBranch {
  id: number;
  name: string;
  face_recognition_enabled: boolean;
  stripe_customer_id?: string | null; // string? in C# is an optional string in TypeScript
  customer_type?: string | null; // string? in C# is an optional string in TypeScript
}

export interface IdentityTopLevelRow {
  face_id: string;
  location_id: number;
  location_name: string;
  branch: string;
  cancel_date: string;
  image_id: string;
  all_activities: number;
  num_activities: number;
  running_total: number | string;
  theft_category: 'High' | 'Medium' | 'Low';
  total: number | string;
}

export interface IdentityRow {
  latest_cancel_date: string;
  running_total: number;
  theft_category: 'High' | 'Medium' | 'Low';
  total: number;
  total_count: number;
  transactions: Pagination<TransactionByFaceId>;
}

export interface IdentityResponse {
  results: IdentityTopLevelRow[];
  count: number;
}

export interface IdentityFilterResponse {
  locations: IdentityLocationFilter[];
}

export interface IdentityLocationFilter {
  branch_id: number;
  branch_name: string;
  location_id: number;
  location_name: string;
  branch_alias_names: string[];
}

export interface UpdateNameResponse {
  firstName: string;
  lastName: string;
}

export interface SharedVideoLinksResponse {
  link: string;
  password: string;
}

export interface ListCamerasRequest {
  search?: string;
  itemsPerPage?: number;
  page?: number;
  sort?: string;
  sortDirection?: string;
  branch: number;
}

export interface GetCameraTimeSeriesRequest extends ListCamerasRequest {
  anchorDate?: Date;
}

export interface ListCamerasResponse {
  items: CameraHealthItem[];
  total_items: number;
}

export interface CameraHealthTimeSeriesPoint {
  date: string;
  count: number;
}

export interface CameraHealthTimeSeriesResult {
  data_points: CameraHealthTimeSeriesPoint[];
  total_cameras: number;
}

export interface CaseAccessedItem {
  email: string;
  last_accessed: string;
}

export interface AccessLogRecord {
  email: string;
  cases_accessed: CaseAccessedItem[];
  roles: (BranchRole | LocationRole)[];
}
